import { Controller } from '@hotwired/stimulus'
import { MercureChannel } from '../realtime/channel.mjs'

export default class extends Controller {
    static values = {
        chatId: Number,
        tokenUrl: String,
        realtimeToken: String,
        phoneConsultationLabel: String,
        sessionExpiredLabel: String,
    }
    
    connect() {
        this.renderedMessageIds = new Set()
        this.pendingSend = false
        
        this.boundTrackRendered = this.trackRenderedMessage.bind(this)
        this.boundTrackSending = () => { this.pendingSend = true }
        // Release suppression on form-handler send failures so a server-broadcast echo still renders.
        this.boundTrackSendFailed = () => { this.pendingSend = false }
        document.addEventListener('litigant-messages:rendered', this.boundTrackRendered)
        document.addEventListener('litigant-messages:sending', this.boundTrackSending)
        document.addEventListener('litigant-messages:send-failed', this.boundTrackSendFailed)
        
        try {
            this.subscription = new MercureChannel({ tokenUrl: this.tokenUrlValue, litigantToken: this.realtimeTokenValue })
            this.subscription.start()
            
            this.subscription.bind('realtime:subscription_error', error => {
                console.error('realtime subscription failed', error)
                const status = typeof error === 'number' ? error : error?.status
                if (status === 403 || status === 401) {
                    const prompt = this.sessionExpiredLabelValue
                        || 'Your secure chat session has expired. Reload to continue?'
                    if (window.confirm(prompt)) {
                        window.location.reload()
                    }
                }
            })
            
            this.lawyerBubbleTemplate = document.getElementById('litigant-chat-bubble-template-lawyer')
            this.leadBubbleTemplate = document.getElementById('litigant-chat-bubble-template')
            this.systemTemplate3 = document.getElementById('litigant-chat-system-template-3')
            this.systemTemplate4 = document.getElementById('litigant-chat-system-template-4')
            this.systemTemplate5 = document.getElementById('litigant-chat-system-template-5')
            this.systemTemplate6Video = document.getElementById('litigant-chat-system-template-6-video')
            this.systemTemplate6Phone = document.getElementById('litigant-chat-system-template-6-phone')
            this.systemTemplate7 = document.getElementById('litigant-chat-system-template-7')
            this.systemTemplate8 = document.getElementById('litigant-chat-system-template-8')
            
            this.subscription.bind('new-message', data => this.handleNewMessage(data))
        } catch (error) {
            console.error('Realtime controller init failed:', error)
        }
    }
    
    disconnect() {
        this.subscription?.unbind_all()
        this.subscription?.disconnect()
        document.removeEventListener('litigant-messages:rendered', this.boundTrackRendered)
        document.removeEventListener('litigant-messages:sending', this.boundTrackSending)
        document.removeEventListener('litigant-messages:send-failed', this.boundTrackSendFailed)
    }
    
    trackRenderedMessage(event) {
        const ids = event.detail?.messageIds || []
        ids.forEach(id => this.renderedMessageIds.add(id))
        this.pendingSend = false
    }
    
    handleNewMessage(data) {
        if (Number(data.chat_id) !== this.chatIdValue) {
            return
        }
        
        if (data.id && this.renderedMessageIds.has(data.id)) {
            return
        }
        
        if (data.sender_type === 'lead' && this.pendingSend) {
            this.renderedMessageIds.add(data.id)
            return
        }
        
        const type = data.type ?? 1

        if (type === 7) {
            return
        }
        
        if (type === 1 || type === 2) {
            if (data.sender_type === 'lead') {
                this.appendLeadBubble(data)
            } else {
                this.appendLawyerBubble(data)
            }
        } else if (type >= 3 && type <= 8) {
            this.appendSystemLabel(data)
        }
        
        const chatContainer = document.querySelector('.litigant-chat__messages')
        if (chatContainer) {
            chatContainer.dispatchEvent(new CustomEvent('litigant-messages:new-message', { bubbles: true }))
        }
    }
    
    appendLawyerBubble(data) {
        if (!this.lawyerBubbleTemplate) return
        this.appendChatBubble(data, 'lawyer', this.lawyerBubbleTemplate)
    }
    
    appendLeadBubble(data) {
        if (!this.leadBubbleTemplate) return
        this.appendChatBubble(data, 'lead', this.leadBubbleTemplate)
    }
    
    /**
     * Renders a new chat bubble. If the most recent row has the same sender_type and
     * is within MERGE_WINDOW_MS, the new content is merged into that bubble. Mirrors
     * the server-side coalesceConsecutiveMessages() and the lawyer-side
     * groupConsecutiveMessages() merges so a "text + file" submit renders as one bubble.
     */
    appendChatBubble(data, senderType, template) {
        const messagesContainer = document.querySelector('.litigant-chat__messages')
        if (!messagesContainer) return

        const wasNearBottom = this.isNearBottom(messagesContainer)
        
        const text = senderType === 'lead' ? (data.message || '') : (data.message ?? '(Nouveau message)')
        const visibleAttachments = (data.file_attachments || []).filter(file => file.is_visible !== false)

        if (!text && visibleAttachments.length === 0) return

        if (this.tryMergeIntoLastBubble(messagesContainer, senderType, data.created_at, text, visibleAttachments)) {
            this.scrollToBottomIfFollowing(messagesContainer, wasNearBottom)
            return
        }
        
        const clone = template.content.cloneNode(true)
        const rowEl = clone.querySelector('.litigant-chat__row')
        if (rowEl && data.created_at) {
            rowEl.setAttribute('data-created-at', data.created_at)
        }
        
        const textContainer = clone.querySelector('.litigant-chat__bubble-text')
        if (textContainer) {
            if (text) {
                textContainer.innerHTML = this.sanitize(text)
            } else {
                textContainer.classList.add('d-none')
            }
        }
        
        if (visibleAttachments.length > 0) {
            const attachmentTemplate = clone.querySelector('.litigant-chat__attachment')
            const contentContainer = clone.querySelector('.litigant-chat__bubble-content')
            
            if (attachmentTemplate && contentContainer) {
                this.populateAttachmentNode(attachmentTemplate, visibleAttachments[0])
                for (let i = 1; i < visibleAttachments.length; i++) {
                    const attachNode = attachmentTemplate.cloneNode(true)
                    this.populateAttachmentNode(attachNode, visibleAttachments[i])
                    contentContainer.appendChild(attachNode)
                }
            }
        }
        
        const timeEl = clone.querySelector('.time-text')
        if (timeEl && data.created_at) {
            timeEl.textContent = this.formatTime(data.created_at)
        }
        
        messagesContainer.appendChild(clone)
        this.scrollToBottomIfFollowing(messagesContainer, wasNearBottom)
    }
    
    /**
     * Merges new content into the previous row if same sender and within MERGE_WINDOW_MS.
     * Window is 60s because the lawyer-side legacy form sends file and text via two
     * separate HTTP requests; the wall-clock gap between realtime events can exceed 5s.
     */
    tryMergeIntoLastBubble(messagesContainer, senderType, createdAtIso, text, visibleAttachments) {
        const MERGE_WINDOW_MS = 60000
        
        const lastRow = messagesContainer.querySelector('.litigant-chat__row:last-child')
        if (!lastRow) return false
        if (lastRow.getAttribute('data-sender-type') !== senderType) return false
        
        const lastTsRaw = lastRow.getAttribute('data-created-at')
        if (!lastTsRaw || !createdAtIso) return false
        
        const lastTs = Date.parse(lastTsRaw.includes('T') ? lastTsRaw : lastTsRaw.replace(' ', 'T'))
        const newTs = Date.parse(createdAtIso.includes('T') ? createdAtIso : createdAtIso.replace(' ', 'T'))
        if (Number.isNaN(lastTs) || Number.isNaN(newTs)) return false
        if (Math.abs(newTs - lastTs) > MERGE_WINDOW_MS) return false

        const lastHasAttachments = lastRow.querySelectorAll('.litigant-chat__attachment:not(.d-none)').length > 0
        const newHasAttachments = visibleAttachments.length > 0
        // The lawyer-side legacy form sends a text+attachment message as two separate
        // HTTP requests (attachment first, then text). Each fires its own realtime event,
        // so the litigant view would render them as two bubbles unless we coalesce when
        // at least one of the two events carries attachments. Two bare text bubbles are
        // intentionally NOT merged so distinct messages stay distinct.
        if (!newHasAttachments && !lastHasAttachments) return false

        const contentContainer = lastRow.querySelector('.litigant-chat__bubble-content')
        if (!contentContainer) return false

        if (text) {
            let textContainer = contentContainer.querySelector('.litigant-chat__bubble-text')
            if (textContainer && !textContainer.innerHTML.trim()) {
                textContainer.innerHTML = this.sanitize(text)
                textContainer.classList.remove('d-none')
            } else {
                const newTextEl = document.createElement('div')
                newTextEl.className = 'litigant-chat__bubble-text'
                newTextEl.innerHTML = this.sanitize(text)
                const firstAttachment = contentContainer.querySelector('.litigant-chat__attachment')
                if (firstAttachment) {
                    contentContainer.insertBefore(newTextEl, firstAttachment)
                } else {
                    contentContainer.appendChild(newTextEl)
                }
            }
        }

        if (visibleAttachments.length > 0) {
            let attachmentTemplate = contentContainer.querySelector('.litigant-chat__attachment.d-none')
            const sourceTemplate = senderType === 'lead' ? this.leadBubbleTemplate : this.lawyerBubbleTemplate
            if (!attachmentTemplate && sourceTemplate) {
                attachmentTemplate = sourceTemplate.content.querySelector('.litigant-chat__attachment')
            }
            if (!attachmentTemplate) return false
            
            visibleAttachments.forEach(file => {
                const attachNode = attachmentTemplate.cloneNode(true)
                attachNode.classList.remove('d-none')
                this.populateAttachmentNode(attachNode, file)
                contentContainer.appendChild(attachNode)
            })
        }
        
        // Advance the bubble's timestamp + visible time to the latest message.
        lastRow.setAttribute('data-created-at', createdAtIso)
        const timeEl = lastRow.querySelector('.litigant-chat__time .time-text')
        if (timeEl) {
            timeEl.textContent = this.formatTime(createdAtIso)
        }
        
        return true
    }
    
    /**
     * Fills .attachment-link, .attachment-name, .attachment-extension, .attachment-size
     * and removes .d-none from a single .litigant-chat__attachment node.
     */
    populateAttachmentNode(attachNode, file) {
        attachNode.classList.remove('d-none')
        
        const fileName = file.file_name || file.name || 'Fichier joint'
        const nameParts = fileName.split('.')
        const extension = file.extension || (nameParts.length > 1 ? nameParts.pop() : '')
        
        const nameEl = attachNode.querySelector('.attachment-name')
        if (nameEl) nameEl.textContent = fileName
        
        const extEl = attachNode.querySelector('.attachment-extension')
        if (extEl) extEl.textContent = extension
        
        const sizeBytes = file.size_bytes || file.size || 0
        const sizeEl = attachNode.querySelector('.attachment-size')
        if (sizeEl && sizeBytes > 0) {
            const sizeMb = Math.ceil((sizeBytes / 1048576) * 10) / 10
            sizeEl.textContent = `${sizeMb} MB`
            sizeEl.classList.remove('d-none')
        }

        const fileUrl = file.litigant_preview_url || file.preview_url || file.url
        const linkEl = attachNode.querySelector('.attachment-link')
        if (linkEl && fileUrl) {
            linkEl.href = fileUrl
            linkEl.setAttribute('download', fileName)
        }
    }
    
    appendSystemLabel(data) {
        let template = null
        
        switch (data.type) {
            case 3:
                template = this.systemTemplate3
                break
            case 4:
                template = this.systemTemplate4
                break
            case 5:
                template = this.systemTemplate5
                break
            case 6:
                template = data.online_consultation_type === 'video'
                    ? this.systemTemplate6Video
                    : this.systemTemplate6Phone
                break
            case 7:
                template = this.systemTemplate7
                break
            case 8:
                template = this.systemTemplate8
                break
        }
        
        if (!template) return
        
        const clone = template.content.cloneNode(true)
        
        const textEl = clone.querySelector('.system-label-text')
        if (textEl) {
            if (data.type === 6 && data.online_consultation_type === 'phone' && this.phoneConsultationLabelValue && data.created_at) {
                textEl.textContent = this.phoneConsultationLabelValue
                    .replace('%date%', this.formatDate(data.created_at))
                    .replace('%hour%', this.formatTime(data.created_at))
            } else {
                textEl.textContent = this.renderTranslatable(data.payment_label_translation) || data.message || ''
            }
        }
        
        const dateEl = clone.querySelector('.system-label-date')
        if (dateEl && data.created_at) {
            dateEl.textContent = this.formatDate(data.created_at) + ' ' + this.formatTime(data.created_at)
        }
        
        // DDT-3869: the type-5 label no longer carries the rejection reason on the litigant side,
        // so there is no .system-label-reason to fill. The lawyer's view still renders it.

        const amountEl = clone.querySelector('.system-label-amount')
        if (amountEl && data.payment && data.payment.amount) {
            const amount = data.payment.amount.replace('.00', '').replace('.', ',')
            amountEl.textContent = ` - ${amount}€`
        }
        
        const linkEl = clone.querySelector('.system-label-link')
        if (linkEl && data.video_consultation_short_url) {
            linkEl.href = data.video_consultation_short_url
        }
        
        const messagesContainer = document.querySelector('.litigant-chat__messages')
        if (messagesContainer) {
            const wasNearBottom = this.isNearBottom(messagesContainer)
            messagesContainer.appendChild(clone)
            this.scrollToBottomIfFollowing(messagesContainer, wasNearBottom)
        }
    }
    
    isNearBottom(container) {
        const NEAR_BOTTOM_THRESHOLD_PX = 100
        return container.scrollHeight - container.scrollTop - container.clientHeight <= NEAR_BOTTOM_THRESHOLD_PX
    }
    
    scrollToBottomIfFollowing(container, wasNearBottom) {
        if (!wasNearBottom) return
        container.scrollTop = container.scrollHeight
    }
    
    sanitize(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>')
    }
    
    formatTime(iso) {
        const date = new Date(iso)
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0')
    }
    
    formatDate(iso) {
        const date = new Date(iso)
        return date.getDate().toString().padStart(2, '0') + '/' + 
               (date.getMonth() + 1).toString().padStart(2, '0') + '/' + 
               date.getFullYear().toString()
    }
    
    formatHour(iso) {
        return this.formatTime(iso)
    }

    renderTranslatable(value) {
        if (typeof value === 'string') return value
        if (value && typeof value === 'object' && typeof value.message === 'string') return value.message
        return ''
    }
}
